/**
 * WebView检测和兼容性处理工具
 */
import { Html5Qrcode } from 'html5-qrcode';
import { createAppMessageListener } from './message-handler';

const HTML5_QRCODE_ELEMENT_ID = 'html5-qrcode-scanner';

/**
 * 检测是否在WebView环境中
 * @returns {Object} WebView环境信息
 */
export function detectWebView() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // React Native WebView的标识
  const isReactNativeWebView = userAgent.includes('ReactNative') || 
                              userAgent.includes('wv') || 
                              userAgent.includes('WebView');
  
  // iOS WebView检测
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  // 更准确的iOS WebView检测
  // 检查是否为Standalone模式（添加到主屏幕的应用）或者明确的WebView标识
  const isIOSWebView = isIOS && (
    (!window.safari && window.navigator.standalone === undefined) || 
    userAgent.includes('WebView') ||
    (window.navigator.standalone !== undefined && !window.navigator.standalone)
  );
  
  // Android WebView检测
  const isAndroid = /Android/.test(userAgent);
  // 更准确的Android WebView检测，主要依赖'wv'标识，这是Android 5.0+ WebView的明确标识
  const isAndroidWebView = isAndroid && /wv/.test(userAgent);
  
  // 更通用的WebView检测
  const isWebView = isReactNativeWebView || isIOSWebView || isAndroidWebView ||
                   (window.ReactNativeWebView !== undefined) ||
                   (window.name === 'webview') ||
                   (window.navigator && window.navigator.standalone !== undefined && !window.navigator.standalone);

  return {
    isWebView,
    isReactNativeWebView,
    isIOSWebView,
    isAndroidWebView,
    userAgent
  };
}

/**
 * 检查localStorage是否可用
 */
function isLocalStorageAvailable() {
  try {
    const testKey = '__storage_test__';
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * 内存存储 - 作为备选方案
 */
class MemoryStorage {
  constructor() {
    this.store = {};
  }

  getItem(key) {
    return this.store[key] || null;
  }

  setItem(key, value) {
    this.store[key] = String(value);
  }

  removeItem(key) {
    delete this.store[key];
  }

  clear() {
    this.store = {};
  }
}

// 根据环境选择合适的存储方案
let storage;
if (isLocalStorageAvailable()) {
  storage = window.localStorage;
} else if (detectWebView().isReactNativeWebView && window.ReactNativeWebView) {
  // 这里我们不使用ReactNativeStorage，而是使用其他存储方案
  storage = new MemoryStorage();
} else {
  storage = new MemoryStorage();
}

// 根据环境选择合适的会话存储方案
let sessionStore;
if (isLocalStorageAvailable()) {
  sessionStore = window.sessionStorage;
} else if (detectWebView().isReactNativeWebView && window.ReactNativeWebView) {
  // 这里我们不使用ReactNativeStorage，而是使用其他存储方案
  sessionStore = new MemoryStorage();
} else {
  sessionStore = new MemoryStorage();
}

/**
 * 安全的localStorage/sessionStorage兼容处理
 */
export class SafeStorage {
  static async getItem(key, useSession = false) {
    try {
      const store = useSession ? sessionStore : storage;
      const result = await store.getItem(key);
      return result;
    } catch (e) {
      console.warn('Storage getItem error:', e);
      return null;
    }
  }
  
  static async setItem(key, value, useSession = false) {
    try {
      const store = useSession ? sessionStore : storage;
      await store.setItem(key, value);
    } catch (e) {
      console.warn('Storage setItem error:', e);
    }
  }
  
  static async removeItem(key, useSession = false) {
    try {
      const store = useSession ? sessionStore : storage;
      await store.removeItem(key);
    } catch (e) {
      console.warn('Storage removeItem error:', e);
    }
  }
  
  static async clear(useSession = false) {
    try {
      const store = useSession ? sessionStore : storage;
      await store.clear();
    } catch (e) {
      console.warn('Storage clear error:', e);
    }
  }
}

/**
 * 安全的tel协议调用
 * @param {string} phoneNumber 电话号码
 */
export function safeCall(phoneNumber) {
  const { isWebView } = detectWebView();
  
  if (isWebView) {
    // 在WebView中，尝试通过postMessage与原生通信
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'TEL',
        payload: {
          phoneNumber
        }
      }));
    } else {
      // 如果无法通过postMessage通信，则尝试直接调用tel协议
      window.location.href = `tel:${phoneNumber}`;
    }
  } else {
    // 在普通浏览器中直接调用tel协议
    window.location.href = `tel:${phoneNumber}`;
  }
}

/**
 * 安全的导航跳转
 * @param {string} url 跳转地址
 */
export function safeNavigate(url) {
  const { isWebView, isReactNativeWebView } = detectWebView();
  
  if (isWebView && isReactNativeWebView) {
    // 在WebView中，尝试通过postMessage与原生通信
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'NAVIGATE',
        payload: {
          url
        }
      }));
    } else {
      // 如果无法通过postMessage通信，则使用window.location
      window.location.href = url;
    }
  } else {
    // 在普通浏览器中直接跳转
    window.location.href = url;
  }
}

/**
 * 安全的新开窗口
 * @param {string} url 新开窗口地址
 * @param {string} target 目标窗口
 * @param {string} features 窗口特性
 */
export function safeOpen(url, target = '_blank', features = '') {
  const { isWebView } = detectWebView();
  
  if (isWebView) {
    // 在WebView中，尝试通过postMessage与原生通信
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'OPEN',
        payload: {
          url,
          target,
          features
        }
      }));
    } else {
      // 如果无法通过postMessage通信，则尝试使用window.open
      try {
        window.open(url, target, features);
      } catch (e) {
        // 如果window.open失败，则使用window.location
        window.location.href = url;
      }
    }
  } else {
    // 在普通浏览器中使用window.open
    window.open(url, target, features);
  }
}

